/**
 * ErrorBoundary — a render crash in ONE panel must not take the whole window with it.
 *
 * Without a boundary anywhere in the tree, any exception thrown while rendering unmounts
 * React's entire root: the app becomes a blank white window with no message, no way back,
 * and (because the crash never reaches the client logger) nothing in the Error log either.
 *
 * The boundary wraps the shell, every route body and every Editor tab body, so a failure is
 * contained to the panel that caused it: the rest of the app — the nav, the other tabs —
 * keeps working, the operator sees what broke, and the failure is LOGGED with its stack.
 *
 * Recovery: React error boundaries latch. Navigating away and back re-keys the boundary
 * (see `App.tsx` / `Editor.tsx`), which remounts it clean; "Reload" is the escape hatch when
 * the panel is the only thing on screen.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { EmptyState } from "../ui/EmptyState.js";
import { useServices, type AppLogger } from "./context.js";
import { ErrorNotice, publicError } from "../ui/ErrorNotice.js";
import { CopyButton } from "../ui/CopyButton.js";

interface BoundaryProps {
  /** The panel this guards, e.g. "Templates" — named in the card and in the log line. */
  name: string;
  children: ReactNode;
  /** Injected by the wrapper below so the class can reach the client logger. */
  logger?: AppLogger;
  /** Whether the panel is on screen. A crashed panel that is shown again retries with a
   *  clean mount instead of latching on the failure for the rest of the session. */
  shown?: boolean;
}

interface BoundaryState {
  error: Error | null;
  stack: string;
}

class ErrorBoundaryInner extends Component<BoundaryProps, BoundaryState> {
  // `override` on all three: `apps/tsconfig.base.json` sets `noImplicitOverride`, so a member
  // that shadows `React.Component` without it is a TS4114 error — which breaks `npm run
  // typecheck` and, with it, `apps/web` + `apps/desktop`'s `tsc --noEmit && vite build`.
  override state: BoundaryState = { error: null, stack: "" };

  static getDerivedStateFromError(error: Error): Partial<BoundaryState> {
    return { error };
  }

  override componentDidUpdate(prev: BoundaryProps): void {
    if (this.state.error && this.props.shown && prev.shown === false) this.setState({ error: null, stack: "" });
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The component stack says WHICH panel/subtree threw — the single most useful thing
    // when the only other evidence was a white window.
    const stack = `${error.stack ?? error.message}\n--- component stack ---${info.componentStack ?? ""}`;
    this.setState({ stack });
    try {
      this.props.logger?.log("error", `${this.props.name} panel crashed — ${error.message}`, {
        panel: this.props.name,
        stack,
      });
    } catch {
      /* a logging failure must never replace the crash card with a second crash */
    }
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    const issue = publicError("panel render failed");
    const details = `${issue.code}: ${issue.description}`;
    return (
      <EmptyState
        icon="warning"
        role="alert"
        testId="error-boundary"
        title="This panel hit an error"
        hint={<span data-testid="error-boundary-message"><ErrorNotice detail="panel render failed" /></span>}
        action={
        <div className="row">
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => { if (typeof window !== "undefined") window.location.reload(); }}
            data-testid="error-boundary-reload"
          >
            Refresh
          </button>
          <CopyButton value={details} label="Copy error details" data-testid="error-boundary-copy">
            Copy details
          </CopyButton>
        </div>
        }
      />
    );
  }
}

/** Read the client logger from the services context and hand it to the class boundary
 *  (class components can't use hooks, and the logger is optional in the DI seam). */
export function ErrorBoundary({ name, shown, children }: { name: string; shown?: boolean; children: ReactNode }) {
  const { logger } = useServices();
  return <ErrorBoundaryInner name={name} logger={logger} shown={shown}>{children}</ErrorBoundaryInner>;
}
